import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarPlot, ChartsAxisHighlight, ChartsLegend, ChartsXAxis, ChartsYAxis } from '@mui/x-charts';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { orderBy } from 'lodash';
import { BoxWithHeader } from '../common';
import { DealerStatistics } from '../types/statistics';
import { DealerAccidentsOverviewProps } from './types';

const processChartData = (data: DealerStatistics) => {
  const dealers: string[] = [];
  const sources: Record<string, number[]> = {};
  const allSourceLabels = new Set<string>();

  const sortedDealers = orderBy(data.totals.countAcceptedByAllDealers.values, ['count'], ['desc']);

  sortedDealers.forEach((dealer) => {
    const dealerLabel = `${dealer.name}`;
    dealers.push(dealerLabel);

    Object.entries(dealer.sources || {}).forEach(([_, source]) => {
      allSourceLabels.add(source.label);
    });
  });

  allSourceLabels.forEach((label) => {
    sources[label] = new Array(dealers.length).fill(0);
  });

  sortedDealers.forEach((dealer, index) => {
    Object.entries(dealer.sources || {}).forEach(([_, source]) => {
      sources[source.label][index] = source.count;
    });
  });

  return { dealers, sources };
};

const colors = ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f'];

export const AllDealersAccidentsReasonsOverview = ({ graphData, date }: DealerAccidentsOverviewProps) => {
  if (!graphData) return null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { dealers, sources } = processChartData(graphData);

  const formatDealerName = (name?: string) => {
    if (!name) return '';
    if (!isMobile) return name;

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];

    const firstName = parts[0];
    const last = parts[parts.length - 1];
    const initial = last[0]?.toUpperCase() ?? '';

    return `${firstName} ${initial}.`;
  };

  const dealerLabels = dealers.map((item) => formatDealerName(item));
  const layout = isMobile ? 'horizontal' : 'vertical';

  const xAxis = isMobile
    ? [
        {
          id: 'x-axis-id',
          scaleType: 'linear'
        } as const
      ]
    : [
        {
          id: 'x-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ];

  const yAxis = isMobile
    ? [
        {
          id: 'y-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ]
    : [
        {
          id: 'y-axis-id',
          scaleType: 'linear'
        } as const
      ];

  const margin = isMobile
    ? { left: 45, right: 10, top: 20, bottom: 150 }
    : { left: 40, right: 20, top: 20, bottom: 170 };

  return (
    <BoxWithHeader
      title={
        <>
          <Typography variant="h6" fontWeight="bold">
            Přehled zdrojů nehod u obchodníků
          </Typography>
          <Typography variant="body2" color="gray">
            {`Celkový přehled zdrojů řešených nehod v čase u obchodníků (${date})`}
          </Typography>
        </>
      }
    >
      <Box sx={{ width: '100%', height: 600 }}>
        <ResponsiveChartContainer
          skipAnimation
          margin={margin}
          series={Object.entries(sources).map(([label, values], idx) => ({
            type: 'bar',
            label,
            data: values,
            stack: 'total',
            color: colors[idx],
            layout
          }))}
          yAxis={yAxis}
          xAxis={xAxis}
        >
          <BarPlot borderRadius={2} barLabel="value" />
          <ChartsXAxis
            position="bottom"
            axisId="x-axis-id"
            label={isMobile ? 'Počet nehod' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 10
                  }
                : {
                    angle: -45,
                    textAnchor: 'end',
                    fontSize: 10
                  }
            }
          />
          <ChartsYAxis
            position="left"
            axisId="y-axis-id"
            label={!isMobile ? 'Počet nehod' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 9,
                    angle: -40
                  }
                : {
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />
          <ChartsAxisHighlight y="band" />
          <ChartsLegend position={{ vertical: 'bottom', horizontal: 'middle' }} />
        </ResponsiveChartContainer>
      </Box>
    </BoxWithHeader>
  );
};
